import { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  RefreshCw,
  PowerOff,
  Check,
  X,
  Repeat,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  fetchCarouselStatus,
  forceCarouselFallback,
  type CarouselStatus,
} from "@/api/carousel";
import { apiErrorMessage } from "@/api/axios";

export interface CarouselUpstreamRef {
  id: number;
  name: string;
}

function fmtTime(iso: string | null): string {
  return iso ? new Date(iso).toLocaleTimeString("ru-RU") : "—";
}

export function CarouselPanel({
  upstreams,
}: {
  upstreams: CarouselUpstreamRef[];
}) {
  const [status, setStatus] = useState<CarouselStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fallbackBusy, setFallbackBusy] = useState(false);
  const [fallbackError, setFallbackError] = useState<string | null>(null);

  const load = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const res = await fetchCarouselStatus();
      setStatus(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setRefreshing(false);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    fetchCarouselStatus()
      .then((res) => {
        if (mounted) setStatus(res.data);
      })
      .catch((e) => {
        if (mounted) console.error(e);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    const interval = setInterval(() => {
      fetchCarouselStatus()
        .then((res) => {
          if (mounted) setStatus(res.data);
        })
        .catch((e) => {
          if (mounted) console.error(e);
        });
    }, 5000);
    return () => {
      mounted = false;
      clearInterval(interval);
    };
  }, []);

  const handleForceFallback = async () => {
    if (
      !confirm(
        "Все клиенты будут немедленно переведены в fallback-режим (трафик пойдёт напрямую, VPN-апстримы отключатся). Продолжить?"
      )
    ) {
      return;
    }
    setFallbackBusy(true);
    setFallbackError(null);
    try {
      const res = await forceCarouselFallback();
      if (res.data.success) {
        await load(true);
      } else {
        setFallbackError(res.data.error || "Ошибка");
      }
    } catch (e: unknown) {
      setFallbackError(apiErrorMessage(e, "Ошибка"));
    } finally {
      setFallbackBusy(false);
    }
  };

  const nameById = new Map(upstreams.map((u) => [u.id, u.name]));
  const activeIds = status?.active_set.ids ?? [];
  const pendingSwap = status?.active_set.pending_swap ?? null;
  const snapshotIds = status
    ? (() => {
        const snaps = status.upstreams;
        const activeOrder = activeIds.filter((id) => snaps[String(id)] != null);
        const activeSet = new Set(activeIds);
        const rest = Object.keys(snaps)
          .map(Number)
          .filter((id) => !activeSet.has(id))
          .sort((a, b) => a - b);
        return [...activeOrder, ...rest].filter((id) => {
          const snap = snaps[String(id)];
          return snap.interface_up || activeSet.has(id);
        });
      })()
    : [];

  return (
    <Card className="border-slate-800 bg-slate-900">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Repeat className="h-4 w-4 text-emerald-400" />
          Карусель апстримов
          {pendingSwap && (
            <span className="text-xs font-normal text-amber-400">
              swap {nameById.get(pendingSwap.from) ?? `#${pendingSwap.from}`} →{" "}
              {nameById.get(pendingSwap.to) ?? `#${pendingSwap.to}`} (осталось
              проверок: {pendingSwap.checks_remaining})
            </span>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => load()}
            disabled={refreshing}
            className="border-slate-700"
          >
            {refreshing ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="mr-2 h-4 w-4" />
            )}
            Обновить
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={handleForceFallback}
            disabled={fallbackBusy}
            className="border-red-900/60 text-red-400 hover:text-red-300 hover:bg-red-950/40"
          >
            {fallbackBusy ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <PowerOff className="mr-2 h-4 w-4" />
            )}
            Force fallback
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {fallbackError && (
          <div className="text-sm text-red-400">Ошибка: {fallbackError}</div>
        )}

        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-400">Active set:</span>
          {activeIds.length === 0 ? (
            <span className="text-xs text-amber-400">
              пусто (fallback-режим)
            </span>
          ) : (
            activeIds.map((id) => (
              <span
                key={id}
                className="inline-flex items-center gap-1 rounded bg-emerald-950/60 border border-emerald-900/60 px-2 py-0.5 text-xs text-emerald-300"
              >
                <Check className="h-3 w-3" />
                {nameById.get(id) ?? `#${id}`}
              </span>
            ))
          )}
        </div>

        <div className="flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-500">
          <span>
            Быстрая проверка:{" "}
            <span className="text-slate-300">
              {fmtTime(status?.last_fast_check_at ?? null)}
            </span>
          </span>
          <span>
            Медленная проверка:{" "}
            <span className="text-slate-300">
              {fmtTime(status?.last_slow_check_at ?? null)}
            </span>
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-slate-400">
                <th className="text-left py-2 px-3">ID</th>
                <th className="text-left py-2 px-3">Название</th>
                <th className="text-left py-2 px-3">Интерфейс</th>
                <th className="text-left py-2 px-3">Latency</th>
                <th className="text-left py-2 px-3">Последняя проверка</th>
                <th className="text-left py-2 px-3">Последний успех</th>
                <th className="text-left py-2 px-3">Ошибка</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-8 text-center text-slate-500"
                  >
                    <Loader2 className="h-6 w-6 animate-spin mx-auto" />
                  </td>
                </tr>
              ) : snapshotIds.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-8 text-center text-slate-500"
                  >
                    Нет данных runtime
                  </td>
                </tr>
              ) : (
                snapshotIds.map((id) => {
                  const snap = status!.upstreams[String(id)];
                  return (
                    <tr
                      key={id}
                      className="border-b border-slate-800/50 hover:bg-slate-800/30"
                    >
                      <td className="py-2 px-3">{id}</td>
                      <td className="py-2 px-3">
                        {nameById.get(id) ?? "—"}
                        {activeIds.includes(id) && (
                          <span className="ml-2 text-xs text-emerald-400">
                            active
                          </span>
                        )}
                      </td>
                      <td className="py-2 px-3">
                        <span
                          className={`inline-flex items-center gap-1 text-xs ${
                            snap.interface_up
                              ? "text-emerald-400"
                              : "text-red-400"
                          }`}
                        >
                          {snap.interface_up ? (
                            <Check className="h-3 w-3" />
                          ) : (
                            <X className="h-3 w-3" />
                          )}
                          {snap.interface_up
                            ? `awg-upstream-${id}`
                            : "down"}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-slate-500">
                        {snap.last_latency_ms != null
                          ? `${snap.last_latency_ms} ms`
                          : "—"}
                      </td>
                      <td className="py-2 px-3 text-slate-500">
                        {fmtTime(snap.last_check_at)}
                      </td>
                      <td className="py-2 px-3 text-slate-500">
                        {fmtTime(snap.last_success)}
                      </td>
                      <td
                        className="py-2 px-3 text-xs text-red-400 max-w-[260px] truncate"
                        title={snap.last_error ?? undefined}
                      >
                        {snap.last_error ?? "—"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
